import React from "react";
import { Eye, ListChecks, Rocket, X } from "lucide-react";
import { formatSearchHistoryDate } from "../../utils/formatDate";
import {
    canCreateCampaignFromSearch,
    canViewCampaignFromSearch,
    getSearchStatusBadge,
} from "../../utils/searchStatusUtils";

const getSearchRecordId = (search) => search?.search_id || search?.id || "";

const SearchHistoryPanel = ({
    isOpen,
    searches = [],
    status = "idle",
    error = "",
    activeSearchId = "",
    isPreparingCampaign = false,
    onClose,
    onSelectSearch,
    onViewCampaigns,
    onCreateCampaign,
}) => {
    if (!isOpen) return null;

    const safeSearches = Array.isArray(searches) ? searches : [];

    return (
        <div className="app-fade-in fixed inset-0 z-[65] flex justify-end bg-slate-900/30">
            <button
                type="button"
                aria-label="Close search history"
                className="absolute inset-0 cursor-default"
                onClick={onClose}
            />
            <aside className="app-panel-slide-right relative z-10 flex h-full w-full max-w-md flex-col bg-white shadow-xl">
                <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
                    <div>
                        <h3 className="text-lg font-semibold text-[#0f172a]">
                            Search History
                        </h3>
                        <p className="text-sm text-[#475569]">
                            Your previous searches
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close search history"
                        title="Close"
                        className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-600 transition hover:bg-slate-50 hover:text-slate-900"
                    >
                        <X size={18} aria-hidden="true" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-5 py-4">
                    {status === "loading" && (
                        <div className="rounded-lg border border-indigo-100 bg-indigo-50 p-4 text-sm text-indigo-800">
                            Loading searches...
                        </div>
                    )}

                    {status === "failed" && (
                        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                            {error || "Failed to load search history."}
                        </div>
                    )}

                    {status !== "loading" &&
                        status !== "failed" &&
                        safeSearches.length === 0 && (
                            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                                No search history yet.
                            </div>
                        )}

                    {status !== "loading" &&
                        status !== "failed" &&
                        safeSearches.length > 0 && (
                            <div className="space-y-2">
                                {safeSearches.map((search) => {
                                    const effectiveSearch = search;
                                    const effectiveSearchId =
                                        getSearchRecordId(effectiveSearch);
                                    const isActiveHistorySearch =
                                        Boolean(effectiveSearchId) &&
                                        effectiveSearchId === activeSearchId;
                                    const statusBadge = getSearchStatusBadge(
                                        effectiveSearch.status,
                                    );
                                    const canCreateCampaign =
                                        canCreateCampaignFromSearch(
                                            effectiveSearch.status,
                                        ) &&
                                        Boolean(effectiveSearchId) &&
                                        !isPreparingCampaign;
                                    const canViewCampaign =
                                        canViewCampaignFromSearch(
                                            effectiveSearch.status,
                                        ) && Boolean(effectiveSearchId);

                                    return (
                                        <div
                                            key={
                                                effectiveSearchId ||
                                                effectiveSearch.query_string
                                            }
                                            className={`rounded-xl border p-3 transition ${
                                                isActiveHistorySearch
                                                    ? "border-orange-200 bg-[#fff7ed] shadow-sm ring-1 ring-orange-100"
                                                    : "border-slate-200 odd:bg-slate-50 even:bg-white hover:border-slate-300 hover:bg-amber-50/50"
                                            }`}
                                        >
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    onSelectSearch?.(
                                                        effectiveSearch,
                                                    )
                                                }
                                                className="w-full text-left"
                                            >
                                                <div className="flex items-start justify-between gap-3">
                                                    <p
                                                        className={`flex min-w-0 items-center gap-2 font-semibold ${
                                                            isActiveHistorySearch
                                                                ? "text-[#7c2d12]"
                                                                : "text-slate-900"
                                                        }`}
                                                    >
                                                        <Eye
                                                            size={15}
                                                            className={`shrink-0 ${
                                                                isActiveHistorySearch
                                                                    ? "text-[#ea580c]"
                                                                    : "text-indigo-500"
                                                            }`}
                                                            aria-hidden="true"
                                                        />
                                                        <span className="min-w-0 truncate">
                                                            {
                                                                effectiveSearch.query_string
                                                            }
                                                        </span>
                                                    </p>
                                                    {statusBadge.label ? (
                                                        <span
                                                            className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${statusBadge.className}`}
                                                        >
                                                            {statusBadge.label}
                                                        </span>
                                                    ) : null}
                                                </div>
                                                <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-500">
                                                    <span>
                                                        {formatSearchHistoryDate(
                                                            effectiveSearch.created_at,
                                                        )}
                                                    </span>
                                                </p>
                                            </button>

                                            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        canViewCampaign &&
                                                        onViewCampaigns?.(
                                                            effectiveSearchId,
                                                        )
                                                    }
                                                    disabled={!canViewCampaign}
                                                    title={
                                                        canViewCampaign
                                                            ? undefined
                                                            : "Search must be completed before viewing campaigns."
                                                    }
                                                    className="inline-flex items-center justify-center gap-2 rounded-lg border border-indigo-100 bg-indigo-50 px-3 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-100 disabled:cursor-not-allowed disabled:opacity-50"
                                                >
                                                    <ListChecks
                                                        size={15}
                                                        aria-hidden="true"
                                                    />
                                                    View Campaigns
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        canCreateCampaign &&
                                                        onCreateCampaign?.(
                                                            effectiveSearch,
                                                        )
                                                    }
                                                    disabled={
                                                        !canCreateCampaign
                                                    }
                                                    title={
                                                        canCreateCampaign
                                                            ? undefined
                                                            : "Search must be completed before creating a campaign."
                                                    }
                                                    className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-indigo-300"
                                                >
                                                    <Rocket
                                                        size={15}
                                                        aria-hidden="true"
                                                    />
                                                    Create Campaign
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                </div>
            </aside>
        </div>
    );
};

export default SearchHistoryPanel;
